/**
 * TrustCipSection — §13c Trust CIP registration fields.
 *
 * Unlike IndividualCipScanSection, trusts DON'T have photo IDs — the trustees
 * do (in their own Individual dialogs). This section captures the Core
 * Director CIP registration fields for the trust itself:
 *
 *   - Field 254 (IdVerifyCode): defaults to `8` = Other (per Bill's §13c open
 *     question A; most conservative choice). TODO(bill): confirm §13c A.
 *   - Field 255 (IdNumber): left BLANK per Bill's §13c guidance B — trusts
 *     don't have ID numbers. Officer can optionally record a document
 *     identifier but the system default is empty. TODO(bill): confirm §13c B.
 *   - Field 256 (IdIssueBy): defaults to BLANK in this section; the Trust
 *     State (governing law) in TrustIdentitySection is the authoritative
 *     source and feeds Field 256 at save-dispatch time. TODO(bill): confirm
 *     §13c C — whether the dispatch should copy from idState or leave blank.
 *   - Field 257 (IdIssueDate): READ-ONLY display — mirrors the Trust
 *     Agreement Date set in TrustIdentitySection (single source of truth).
 *   - Field 258 (IdExpiryDate): BLANK — trusts are perpetual, so no
 *     expiry date is recorded. TODO(bill): confirm §13c D — card mentions
 *     '00/00/0000' as a candidate but Core Director may prefer blank.
 *
 * The section also surfaces Hard-required document upload slots for
 * TRUST_AGREEMENT, TRUST_SUCCESSOR_PAGE, TRUST_SIGNATURE_PAGE, and
 * TRUST_CERT via DocumentSlotsSection. Those are filtered to a Trust-only
 * subset — the composition root's DocumentSlotsSection still renders the
 * full sub-type catalog for any other per-sub-type docs (EIN_LETTER, etc).
 *
 * NO scan panel here — §13c explicitly states trust agreement is uploaded
 * manually via the document slots grid, not captured via DL / camera pipeline.
 */

import { CollapsibleSection } from '@/components/primitives';
import type { RaoPerson } from '../../../types';
import type { AttachedDoc } from '../shared/attachedDocs';
import { ID_TYPE_CODES } from '../individual/individual-config';
import { TRUST_HARD_DOC_CODES } from './trust-config';

// -----------------------------------------------------------------------------
// Styles
// -----------------------------------------------------------------------------

const labelStyle: React.CSSProperties = {
  fontSize: 12,
  color: 'var(--ink-600)',
  display: 'block',
  marginBottom: 4,
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  borderRadius: 4,
  border: '1px solid var(--ink-200)',
  fontSize: 14,
  boxSizing: 'border-box',
};

const readOnlyStyle: React.CSSProperties = {
  ...inputStyle,
  background: 'var(--field-readonly-bg, #f5f5f5)',
  color: 'var(--ink-700)',
};

function changedFieldStyle(base: React.CSSProperties, changed: boolean): React.CSSProperties {
  return changed
    ? { ...base, borderLeft: '3px solid var(--pcb-blue, #2968ad)', background: 'var(--field-changed-bg, #f0f7ff)', color: 'var(--ink-900, #1d1d1f)' }
    : base;
}

// -----------------------------------------------------------------------------
// Extra-field helpers
// -----------------------------------------------------------------------------

function readExtra(person: RaoPerson, key: string): string {
  const v = person.extra?.[key];
  return typeof v === 'string' ? v : '';
}

function patchExtra(person: RaoPerson, patch: Record<string, unknown>): Partial<RaoPerson> {
  return { extra: { ...(person.extra ?? {}), ...patch } };
}

// -----------------------------------------------------------------------------
// Props
// -----------------------------------------------------------------------------

export interface TrustCipSectionProps {
  person: RaoPerson;
  onChange: (patch: Partial<RaoPerson>) => void;
  mode: 'new' | 'existing';
  isChanged?: (field: string) => boolean;
  readOnly?: boolean;
  /**
   * Lifted dialog-level attachments. The CIP section only *reads* these to
   * render a missing/attached checklist for the Hard-required trust docs;
   * actual uploads happen in the shared DocumentSlotsSection at the bottom
   * of the dialog.
   */
  attachedDocs?: ReadonlyArray<AttachedDoc>;
}

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

export function TrustCipSection({
  person,
  onChange,
  mode,
  isChanged,
  readOnly = false,
  attachedDocs,
}: TrustCipSectionProps): JSX.Element {
  const ic = isChanged ?? ((_: string) => false);

  // Field 254: default to '8' = Other (conservative choice per card §13c A).
  // TODO(bill): confirm §13c A — is '8' the right IdVerifyCode for Trust,
  //             or does PCB have an extended code we should add to
  //             ID_TYPE_CODES?
  const idType = readExtra(person, 'idType') || '8';

  // Field 255: blank default (per Bill's §13c guidance B — trusts don't
  // have ID numbers). Officer can optionally type a document identifier
  // (e.g., recorder volume/page, internal doc ID). Left empty by default.
  // TODO(bill): confirm §13c B — default blank vs trust-name vs doc-ID.
  const idNumber = readExtra(person, 'idNumber');

  // Field 256: blank default in this section. The Trust State dropdown in
  // TrustIdentitySection is the authoritative value; the save-dispatch
  // (useSaveDispatch) can copy idState → Field 256 at commit time.
  // TODO(bill): confirm §13c C — copy idState into 256, or leave blank?
  const idIssuer = readExtra(person, 'idIssuer');

  // Field 257: Trust agreement date. Mirrored from person.dob (single source
  // of truth in TrustIdentitySection) so edits there flow through here.
  const idIssueDate = person.dob ?? readExtra(person, 'idIssueDate');

  // Field 258: blank default (trusts are perpetual). Card mentions
  // '00/00/0000' candidate but Core Director may prefer empty.
  // TODO(bill): confirm §13c D — blank vs '00/00/0000' vs a far-future date.
  const idExpiryDate = readExtra(person, 'idExpiryDate');

  const isExisting = mode === 'existing';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <CollapsibleSection
        title="Trust CIP Registration"
        iconColor="var(--pcb-red, #b10505)"
        defaultOpen={!isExisting}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {/* Officer guidance banner */}
          <div
            style={{
              padding: '8px 12px',
              background: 'var(--info-bg)',
              border: '1px solid var(--pcb-blue)',
              borderRadius: 6,
              fontSize: 12,
              color: 'var(--ink-900)',
            }}
          >
            Trusts don&rsquo;t have photo IDs — trustees do, in their own Individual dialogs.
            This section captures the trust&rsquo;s Core Director CIP registration fields and
            surfaces the required trust-document upload slots below.
          </div>

          {/* Field 254 / 255 row */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={labelStyle} htmlFor="rao-trust-idtype">
                ID Verify Code (Field 254)
              </label>
              <select
                id="rao-trust-idtype"
                value={idType}
                onChange={(e) => onChange(patchExtra(person, { idType: e.currentTarget.value }))}
                disabled={readOnly}
                style={changedFieldStyle(readOnly ? readOnlyStyle : inputStyle, ic('idType'))}
                title="ID Verify Code — default 8 (Other) per §13c. TODO(bill): confirm §13c A."
              >
                {ID_TYPE_CODES.map((t) => (
                  <option key={t.value || 'none'} value={t.value}>{t.label}</option>
                ))}
              </select>
              <div style={{ fontSize: 11, color: 'var(--ink-600)', marginTop: 2, fontStyle: 'italic' }}>
                Default &lsquo;8 · Other&rsquo; (conservative; pending Bill §13c A clarification).
              </div>
            </div>
            <div>
              <label style={labelStyle} htmlFor="rao-trust-idnum">
                ID Number (Field 255)
              </label>
              <input
                id="rao-trust-idnum"
                type="text"
                value={idNumber}
                onChange={(e) => onChange(patchExtra(person, { idNumber: e.currentTarget.value }))}
                readOnly={readOnly}
                style={changedFieldStyle(readOnly ? readOnlyStyle : inputStyle, ic('idNumber'))}
                maxLength={30}
                placeholder="(leave blank — trusts have no ID number)"
                title="ID Number — default blank per §13c B. TODO(bill): confirm §13c B."
              />
              <div style={{ fontSize: 11, color: 'var(--ink-600)', marginTop: 2, fontStyle: 'italic' }}>
                Optional — trusts don&rsquo;t have ID numbers; system default is blank.
              </div>
            </div>
          </div>

          {/* Field 256 / 257 / 258 row */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            <div>
              <label style={labelStyle} htmlFor="rao-trust-idissuer">
                ID Issued By (Field 256)
              </label>
              <input
                id="rao-trust-idissuer"
                type="text"
                value={idIssuer}
                onChange={(e) => onChange(patchExtra(person, { idIssuer: e.currentTarget.value }))}
                readOnly={readOnly}
                style={changedFieldStyle(readOnly ? readOnlyStyle : inputStyle, ic('idIssuer'))}
                maxLength={20}
                placeholder="(defaults to Trust State at save)"
                title="ID Issued By — left blank; Trust State from identity section copies in at commit. TODO(bill): confirm §13c C."
              />
              <div style={{ fontSize: 11, color: 'var(--ink-600)', marginTop: 2, fontStyle: 'italic' }}>
                Copies from Trust State (identity) at save.
              </div>
            </div>
            <div>
              <label style={labelStyle} htmlFor="rao-trust-idissue">
                ID Issue Date (Field 257)
              </label>
              <input
                id="rao-trust-idissue"
                type="date"
                value={idIssueDate}
                readOnly
                style={readOnlyStyle}
                title="Trust Agreement Date — edit above in the identity section."
              />
              <div style={{ fontSize: 11, color: 'var(--ink-600)', marginTop: 2, fontStyle: 'italic' }}>
                Mirrors Trust Agreement Date (identity section).
              </div>
            </div>
            <div>
              <label style={labelStyle} htmlFor="rao-trust-idexp">
                ID Expiry Date (Field 258)
              </label>
              <input
                id="rao-trust-idexp"
                type="date"
                value={idExpiryDate}
                onChange={(e) => onChange(patchExtra(person, { idExpiryDate: e.currentTarget.value }))}
                readOnly={readOnly}
                style={changedFieldStyle(readOnly ? readOnlyStyle : inputStyle, ic('idExpiryDate'))}
                title="Expiry Date — default blank (trusts are perpetual). TODO(bill): confirm §13c D."
              />
              <div style={{ fontSize: 11, color: 'var(--ink-600)', marginTop: 2, fontStyle: 'italic' }}>
                Default blank (perpetual).
              </div>
            </div>
          </div>
        </div>
      </CollapsibleSection>

      {/* Trust-required document upload slots — real slot UI lives in the
          composition root's DocumentSlotsSection (shared, at bottom of dialog).
          Here we just show a checklist/advisory panel pointing at it so the
          officer sees the CIP-level doc expectations near the other CIP fields. */}
      <CollapsibleSection
        title="Trust Agreement & Registration Documents"
        iconColor="var(--pcb-red, #b10505)"
        defaultOpen
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div
            style={{
              padding: '8px 12px',
              background: 'var(--info-bg)',
              border: '1px solid var(--pcb-blue)',
              borderRadius: 6,
              fontSize: 12,
              color: 'var(--ink-900)',
            }}
          >
            <strong>Hard-required uploads:</strong> Trust Agreement, Successor Trustee Page,
            Signature Page, and Certification of Trust (notarized). Upload via the
            Document Requirements grid at the bottom of this dialog — at least one of{' '}
            <code>TRUST_AGREEMENT</code> or <code>TRUST_CERT</code> must be attached
            before commit.
          </div>

          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: 'var(--ink-700)' }}>
            {TRUST_HARD_DOC_CODES.map((code) => {
              const fulfilled = (attachedDocs ?? []).some((d) => d.docCode === code);
              return (
                <li key={code} style={{ marginBottom: 2 }}>
                  <code>{code}</code>{' '}
                  {fulfilled ? (
                    <span style={{ color: 'var(--pcb-green, #386732)', fontWeight: 600 }}>
                      attached
                    </span>
                  ) : (
                    <span style={{ color: 'var(--pcb-red, #b10505)' }}>missing</span>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      </CollapsibleSection>
    </div>
  );
}
